import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BILLING_WARN_PERCENT_OPTIONS,
  DIALING_CODES,
  toE164,
  toNsn,
  validateBillingSmsSettings,
  type BillingSmsRecipientMode,
  type BillingSmsSettings,
} from '@acs/shared';
import { Card, Checkbox, Field, Input, SaveBar, Select, Switch, useToast } from '@acs/ui';
import { useActiveBusiness } from '../../app/ActiveBusinessLayout.js';
import { useI18n } from '../../i18n/index.js';
import type { TranslationKey } from '../../i18n/index.js';
import { billingErrorDetails } from '../../shared/api/billing.js';
import { declaredSourceNumber } from '../../shared/lib/businessNumber.js';
import { formatPhoneDisplay } from '../../shared/lib/phoneFormat.js';
import { billingError } from '../billing/billingText.js';
import { useSaveBillingSms } from '../billing/prepaidHooks.js';

/**
 * "SMS about the balance" — messages to the OWNER about money (doc 42 §5).
 *
 * Its own card, and NOT behind the client-SMS master switch: that switch is
 * about what a business's clients receive. Everything is off by default, and
 * every change goes through `saveBillingSmsSettings` — the server normalises
 * the number, re-checks first > second and the revision; the rules forbid a
 * direct write.
 *
 * The recipient is the owner's business line or a number they type. Never the
 * AI number calls are diverted to: an SMS there reaches nobody.
 */
const COUNTRIES = Object.keys(DIALING_CODES);

interface FormState {
  enabled: boolean;
  recipientMode: BillingSmsRecipientMode;
  customPhone: string;
  customCountry: string;
  firstOn: boolean;
  firstPct: number;
  secondOn: boolean;
  secondPct: number;
  onTopup: boolean;
}

function fromStored(s: BillingSmsSettings, country: string): FormState {
  const iso = COUNTRIES.find((c) => s.customPhone.startsWith(`+${DIALING_CODES[c]}`)) ?? country;
  return {
    enabled: s.enabled,
    recipientMode: s.recipientMode,
    customPhone: s.customPhone,
    customCountry: iso,
    firstOn: s.first.enabled,
    firstPct: s.first.remainingPercent,
    secondOn: s.second.enabled,
    secondPct: s.second.remainingPercent,
    onTopup: s.onTopup,
  };
}

export function BillingNotificationsCard() {
  const { t } = useI18n();
  const business = useActiveBusiness();
  const { toast } = useToast();
  const save = useSaveBillingSms(business.id);
  const stored = business.notifications.billingSms;
  const [form, setForm] = useState(() => fromStored(stored, business.country));
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  // Server-only errors describe the submitted values, not every future edit.
  // Clear them synchronously with an edit so a corrected number can be saved.
  const editForm = (next: FormState) => {
    setServerErrors({});
    setForm(next);
  };

  // Switching business, or another tab saving, must not leave a stale form
  // with another business's number in it (doc 42 W7).
  useEffect(() => {
    setForm(fromStored(stored, business.country));
    setServerErrors({});
  }, [business.id, stored.revision]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep link from Settings › Billing (`#billing-sms`).
  useEffect(() => {
    if (window.location.hash === '#billing-sms') document.getElementById('billing-sms')?.scrollIntoView({ block: 'start' });
  }, []);

  const businessNumber = declaredSourceNumber(business);
  const parts = form.customPhone.trim() ? toNsn(form.customPhone, form.customCountry) : null;
  const customE164 = parts ? toE164(parts) : null;
  const candidate: BillingSmsSettings = {
    enabled: form.enabled,
    recipientMode: form.recipientMode,
    customPhone: form.customPhone,
    first: { enabled: form.firstOn, remainingPercent: form.firstPct },
    second: { enabled: form.secondOn, remainingPercent: form.secondPct },
    onTopup: form.onTopup,
    revision: stored.revision,
  };
  const errors = { ...validateBillingSmsSettings(candidate, { customPhoneE164: customE164, businessNumber }), ...serverErrors };
  const err = (k: string) => (errors[k as keyof typeof errors] ? t(`billingSms.err.${errors[k as keyof typeof errors]}` as TranslationKey) : undefined);

  const pristine = fromStored(stored, business.country);
  const dirty = JSON.stringify(form) !== JSON.stringify(pristine);
  const invalid = Object.keys(errors).length > 0;

  const percentOptions = BILLING_WARN_PERCENT_OPTIONS.map((p) => ({ value: String(p), label: `${p}%` }));

  const submit = () => {
    setServerErrors({});
    save.mutate(
      {
        enabled: form.enabled,
        recipientMode: form.recipientMode,
        customPhone: form.customPhone,
        customPhoneCountry: form.customCountry,
        first: { enabled: form.firstOn, remainingPercent: form.firstPct },
        second: { enabled: form.secondOn, remainingPercent: form.secondPct },
        onTopup: form.onTopup,
        expectedRevision: stored.revision,
      },
      {
        onSuccess: () => toast({ title: t('billingSms.saved'), tone: 'success' }),
        onError: (e) => {
          setServerErrors(billingErrorDetails(e).fields);
          toast({ title: billingError(t, e), tone: 'danger' });
        },
      },
    );
  };

  return (
    <Card title={t('billingSms.title')} id="billing-sms">
      <p className="field-hint" style={{ margin: '0 0 16px' }}>{t('billingSms.hint')}</p>

      <Switch checked={form.enabled} onChange={(e) => editForm({ ...form, enabled: e.target.checked })} label={t('billingSms.enabled')} />

      <div style={{ display: 'grid', gap: 16, marginTop: 16, maxWidth: 460 }}>
        <Field label={t('billingSms.recipient')} error={err('recipient')}>
          <Select
            value={form.recipientMode}
            onChange={(e) => editForm({ ...form, recipientMode: e.target.value as BillingSmsRecipientMode })}
            options={[
              { value: 'business_number', label: t('billingSms.recipient.business') },
              { value: 'custom', label: t('billingSms.recipient.custom') },
            ]}
          />
        </Field>

        {form.recipientMode === 'business_number' ? (
          <p className="field-hint" style={{ margin: 0 }}>
            {businessNumber ? t('billingSms.businessNumber', { number: formatPhoneDisplay(businessNumber) ?? businessNumber }) : t('billingSms.noBusinessNumber')}
          </p>
        ) : (
          <Field label={t('billingSms.customPhone')} hint={t('billingSms.customHint')} error={err('customPhone')}>
            <div className="row" style={{ gap: 8 }}>
              <Select
                aria-label={t('billingSms.country')}
                value={form.customCountry}
                onChange={(e) => editForm({ ...form, customCountry: e.target.value })}
                options={COUNTRIES.map((c) => ({ value: c, label: `${c} +${DIALING_CODES[c]}` }))}
                style={{ width: 120 }}
              />
              <Input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                mono
                value={form.customPhone}
                invalid={!!err('customPhone')}
                onChange={(e) => editForm({ ...form, customPhone: e.target.value })}
                style={{ flex: 1 }}
              />
            </div>
          </Field>
        )}

        <div className="row" style={{ gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Checkbox checked={form.firstOn} onChange={(e) => editForm({ ...form, firstOn: e.target.checked })} label={t('billingSms.first')} />
          <Select aria-label={t('billingSms.first')} value={String(form.firstPct)} options={percentOptions} onChange={(e) => editForm({ ...form, firstPct: Number(e.target.value) })} style={{ width: 96 }} />
        </div>
        {err('first') && <p className="field-error" role="alert" style={{ margin: 0 }}>{err('first')}</p>}
        <div className="row" style={{ gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Checkbox checked={form.secondOn} onChange={(e) => editForm({ ...form, secondOn: e.target.checked })} label={t('billingSms.second')} />
          <Select aria-label={t('billingSms.second')} value={String(form.secondPct)} options={percentOptions} onChange={(e) => editForm({ ...form, secondPct: Number(e.target.value) })} style={{ width: 96 }} />
        </div>
        {err('second') && <p className="field-error" role="alert" style={{ margin: 0 }}>{err('second')}</p>}
        <Checkbox checked={form.onTopup} onChange={(e) => editForm({ ...form, onTopup: e.target.checked })} label={t('billingSms.onTopup')} />

        <p className="field-hint" style={{ margin: 0 }}>{t('billingSms.percentHelp')}</p>
        <p className="field-hint" style={{ margin: 0 }}>{t('billingSms.anyTime')}</p>
        <Link to={`/b/${business.id}/settings?section=billing`}>{t('billingSms.toBilling')}</Link>
      </div>

      {dirty && (
        <SaveBar
          saveLabel={t('common.saveChanges')}
          discardLabel={t('common.discard')}
          regionLabel={t('ui.unsavedChanges')}
          message={invalid ? t('billingSms.fixErrors') : t('ui.unsavedChanges')}
          dirty={dirty}
          saveDisabled={invalid}
          saving={save.isPending}
          onDiscard={() => {
            setForm(pristine);
            setServerErrors({});
          }}
          onSave={submit}
        />
      )}
    </Card>
  );
}
